import type { PipelineStats } from '../audio/pipeline';

function formatBitrate(bitsPerSecond: number): string {
  if (bitsPerSecond <= 0) return '—';
  if (bitsPerSecond >= 1_000_000) return `${(bitsPerSecond / 1_000_000).toFixed(2)} Mb/s`;
  return `${Math.round(bitsPerSecond / 1000)} kb/s`;
}

const DECODER_LABEL: Record<string, string> = {
  webcodecs: 'Opus (WebCodecs)',
  wasm: 'Opus (WASM)',
  pcm: 'PCM f32',
};

/**
 * The unglamorous numbers. They live at the bottom of the panel because they only
 * matter when something sounds wrong — and then they are the first thing to look at.
 */
export function StatsGrid({
  stats,
  listeners,
  rttMs,
}: {
  stats: PipelineStats;
  listeners: number;
  rttMs: number | null;
}) {
  return (
    <dl className="stats">
      <div className="stat">
        <dt>Listeners</dt>
        <dd>{listeners}</dd>
      </div>
      <div className="stat">
        <dt>Bitrate</dt>
        <dd>{formatBitrate(stats.bitrateBps)}</dd>
      </div>
      <div className="stat">
        <dt>Round trip</dt>
        <dd>{rttMs === null ? '—' : `${rttMs} ms`}</dd>
      </div>
      <div className="stat">
        <dt>Decoder</dt>
        <dd>{stats.decoder ? (DECODER_LABEL[stats.decoder] ?? stats.decoder) : '—'}</dd>
      </div>
      <div className="stat">
        <dt>Buffer</dt>
        <dd>
          {Math.round(stats.bufferedMs)} ms
          {stats.bufferState === 'filling' ? ' (filling)' : ''}
        </dd>
      </div>
      <div className="stat">
        <dt>Underruns</dt>
        <dd data-tone={stats.underruns > 0 ? 'warn' : undefined}>{stats.underruns}</dd>
      </div>
      <div className="stat">
        <dt>Dropped frames</dt>
        <dd data-tone={stats.lostFrames > 0 ? 'warn' : undefined}>
          {stats.lostFrames}
          {stats.concealedFrames > 0 ? ` (${stats.concealedFrames} concealed)` : ''}
        </dd>
      </div>
      <div className="stat">
        <dt>Trimmed</dt>
        <dd>{Math.round(stats.discardedMs)} ms</dd>
      </div>
      {stats.resampling ? (
        <div className="stat">
          <dt>Resampling</dt>
          <dd data-tone="warn">{Math.round(stats.contextSampleRate)} Hz</dd>
        </div>
      ) : null}
      {stats.malformedFrames > 0 ? (
        <div className="stat">
          <dt>Bad frames</dt>
          <dd data-tone="warn">{stats.malformedFrames}</dd>
        </div>
      ) : null}
    </dl>
  );
}
